import styled from '@emotion/styled';
import { Button } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { FiArrowRight, FiBookOpen, FiCheck, FiMove, FiTarget } from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import logo from '../../../assets/images/logo_trans.png';
import { ROUTES } from '../../../routes';
import { ORANGE, PRIMARY, YELLOW } from '../../../shared/colors';
import { Layout } from '../../shared/Layout';

const Container = styled.div`
  min-height: calc(100vh - 132px);
  background:
    radial-gradient(circle at 8% 0%, rgba(255, 79, 31, 0.12), transparent 25%),
    radial-gradient(circle at 88% 8%, rgba(255, 176, 0, 0.1), transparent 22%),
    var(--mt-bg);
  color: var(--mt-text);
  font-family: Poppins-R;

  .learning-hero {
    position: relative;
    overflow: hidden;
    border-bottom: 1px solid var(--mt-border);
    background:
      linear-gradient(135deg, rgba(255, 79, 31, 0.16), transparent 34%),
      ${PRIMARY};
    color: #fff3dc;
  }

  .learning-hero::after {
    content: '';
    position: absolute;
    right: -80px;
    bottom: -160px;
    width: 420px;
    height: 420px;
    background-image: url(${logo});
    background-position: center;
    background-repeat: no-repeat;
    background-size: contain;
    opacity: 0.09;
    pointer-events: none;
  }

  .hero-inner {
    position: relative;
    z-index: 1;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 32px;
    align-items: end;
    padding: 38px clamp(20px, 5vw, 72px) 34px;
  }

  .brand-row {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 22px;
  }

  .brand-row img {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    object-fit: contain;
  }

  .brand-row strong {
    color: #fff3dc;
    font-family: Poppins-B;
    font-size: 20px;
  }

  .brand-row .orange {
    color: ${ORANGE};
  }

  .brand-row .gold {
    color: ${YELLOW};
  }

  .hero-kicker {
    color: ${YELLOW};
    font-family: Poppins-B;
    font-size: 11px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }

  .hero-title {
    max-width: 840px;
    margin: 8px 0 0;
    color: #f8fafc;
    font-family: Poppins-B;
    font-size: clamp(34px, 4.2vw, 58px);
    line-height: 1.02;
  }

  .hero-copy {
    max-width: 720px;
    margin: 16px 0 0;
    color: rgba(255, 243, 220, 0.78);
    font-size: 15px;
    line-height: 1.7;
  }

  .hero-status {
    min-width: 250px;
    border: 1px solid rgba(255, 255, 255, 0.14);
    border-radius: 8px;
    padding: 14px 16px;
    background: rgba(8, 20, 30, 0.58);
  }

  .hero-status span {
    display: block;
    color: rgba(255, 243, 220, 0.62);
    font-size: 11px;
    text-transform: uppercase;
  }

  .hero-status strong {
    display: block;
    margin-top: 6px;
    color: ${YELLOW};
    font-family: Poppins-B;
    font-size: 24px;
  }

  .progress-track {
    height: 6px;
    margin-top: 12px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.14);
    overflow: hidden;
  }

  .progress-fill {
    height: 100%;
    border-radius: inherit;
    background: linear-gradient(90deg, ${ORANGE}, ${YELLOW});
  }

  .score-strip {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 16px;
  }

  .score-pill {
    border: 1px solid rgba(255, 176, 0, 0.24);
    border-radius: 999px;
    padding: 6px 10px;
    color: #fff3dc;
    background: rgba(8, 20, 30, 0.5);
    font-size: 12px;
  }

  .score-pill strong {
    color: ${YELLOW};
    font-family: Poppins-B;
  }

  .learning-content {
    display: grid;
    grid-template-columns: minmax(260px, 0.78fr) minmax(0, 1.22fr);
    gap: 12px;
    padding: 12px;
  }

  .panel {
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    background: var(--mt-card);
    box-shadow: var(--mt-card-shadow);
    overflow: hidden;
  }

  .panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-height: 42px;
    padding: 10px 14px;
    background:
      linear-gradient(135deg, rgba(255, 79, 31, 0.14), transparent 44%),
      ${PRIMARY};
    color: ${YELLOW};
    font-family: Poppins-B;
    text-transform: uppercase;
  }

  .lesson-list {
    display: grid;
    gap: 8px;
    padding: 12px;
  }

  .roadmap {
    display: grid;
    gap: 8px;
    padding: 0 12px 12px;
  }

  .roadmap-title {
    color: var(--mt-muted);
    font-family: Poppins-B;
    font-size: 10px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .roadmap-step {
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 9px 10px;
    background: var(--mt-card);
  }

  .roadmap-step strong {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    color: var(--mt-text);
    font-family: Poppins-B;
    font-size: 12px;
  }

  .roadmap-step span {
    color: var(--mt-warning);
  }

  .roadmap-step p {
    margin: 4px 0 0;
    color: var(--mt-muted);
    font-size: 11px;
    line-height: 1.4;
  }

  .level-tabs {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    padding: 12px 12px 0;
  }

  .level-tab {
    border: 1px solid var(--mt-border);
    border-radius: 999px;
    padding: 8px 10px;
    background: var(--mt-surface);
    color: var(--mt-muted);
    cursor: pointer;
    font-family: Poppins-B;
    font-size: 11px;
    text-align: center;
  }

  .level-tab.active {
    border-color: rgba(255, 176, 0, 0.55);
    color: var(--mt-warning);
    background: var(--mt-row-hover);
  }

  .lesson-card {
    width: 100%;
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 12px;
    background: var(--mt-surface);
    color: var(--mt-text);
    cursor: pointer;
    text-align: left;
    transition:
      border-color 0.16s ease,
      transform 0.16s ease,
      background-color 0.16s ease;
  }

  .lesson-card:hover,
  .lesson-card.active {
    border-color: rgba(255, 176, 0, 0.52);
    background: var(--mt-row-hover);
    transform: translateY(-1px);
  }

  .lesson-card.completed {
    box-shadow: inset 3px 0 0 #008f4f;
  }

  .lesson-card span {
    color: var(--mt-warning);
    font-family: Poppins-B;
    font-size: 10px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .lesson-card .lesson-meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .lesson-card .level-badge {
    border: 1px solid rgba(255, 176, 0, 0.22);
    border-radius: 999px;
    padding: 3px 7px;
    color: var(--mt-muted);
    font-size: 9px;
    letter-spacing: 0.04em;
  }

  .lesson-card strong {
    display: block;
    margin-top: 4px;
    font-family: Poppins-B;
    font-size: 15px;
  }

  .lesson-card p {
    margin: 5px 0 0;
    color: var(--mt-muted);
    font-size: 12px;
    line-height: 1.45;
  }

  .lesson-stage {
    padding: 14px;
  }

  .tip-screen {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 280px;
    gap: 12px;
  }

  .tip-main,
  .quiz-box,
  .drag-box,
  .side-note,
  .certificate-box,
  .quick-links,
  .detail-panel,
  .practice-box {
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    background: var(--mt-surface);
  }

  .tip-main {
    min-height: 250px;
    padding: 22px;
  }

  .tip-main .eyebrow,
  .side-note .eyebrow,
  .quiz-box .eyebrow,
  .drag-box .eyebrow,
  .detail-panel .eyebrow,
  .practice-box .eyebrow,
  .certificate-box .eyebrow,
  .quick-links .eyebrow {
    color: var(--mt-warning);
    font-family: Poppins-B;
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .tip-main h2 {
    margin: 8px 0 10px;
    color: var(--mt-text);
    font-family: Poppins-B;
    font-size: 30px;
    line-height: 1.1;
  }

  .tip-main p {
    max-width: 760px;
    margin: 0;
    color: var(--mt-muted);
    font-size: 15px;
    line-height: 1.75;
  }

  .tip-points {
    display: grid;
    gap: 8px;
    margin-top: 20px;
  }

  .tip-point {
    display: flex;
    align-items: flex-start;
    gap: 9px;
    color: var(--mt-text);
    font-size: 13px;
  }

  .tip-point svg {
    flex: 0 0 auto;
    margin-top: 2px;
    color: #008f4f;
  }

  .side-note {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 18px;
  }

  .side-note strong {
    display: block;
    margin-top: 8px;
    color: var(--mt-text);
    font-family: Poppins-B;
    font-size: 20px;
    line-height: 1.25;
  }

  .side-note p {
    margin: 12px 0 0;
    color: var(--mt-muted);
    line-height: 1.55;
  }

  .lesson-actions {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    margin-top: 12px;
  }

  .detail-panel {
    margin-top: 12px;
    padding: 16px;
  }

  .detail-panel h3,
  .practice-box h3 {
    margin: 6px 0 10px;
    color: var(--mt-text);
    font-family: Poppins-B;
    font-size: 20px;
  }

  .detail-panel p,
  .practice-box p {
    margin: 0;
    color: var(--mt-muted);
    line-height: 1.6;
  }

  .detail-metrics {
    display: grid;
    grid-template-columns: 0.85fr 1.15fr;
    gap: 10px;
    margin-top: 12px;
  }

  .detail-metric {
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 12px;
    background: var(--mt-card);
  }

  .detail-metric span {
    display: block;
    color: var(--mt-warning);
    font-family: Poppins-B;
    font-size: 10px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .detail-metric strong {
    display: block;
    margin-top: 6px;
    color: var(--mt-text);
    font-family: Poppins-B;
    line-height: 1.45;
  }

  .learning-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(320px, 0.9fr);
    gap: 12px;
    margin-top: 12px;
  }

  .quiz-box,
  .drag-box,
  .certificate-box,
  .quick-links,
  .practice-box {
    padding: 16px;
  }

  .quiz-box h3,
  .drag-box h3,
  .certificate-box h3,
  .quick-links h3 {
    margin: 6px 0 12px;
    color: var(--mt-text);
    font-family: Poppins-B;
    font-size: 18px;
  }

  .practice-box {
    margin-top: 12px;
  }

  .practice-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 8px;
    margin-top: 12px;
  }

  .practice-field {
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 10px;
    background: var(--mt-card);
  }

  .practice-field span {
    display: block;
    color: var(--mt-muted);
    font-size: 10px;
    text-transform: uppercase;
  }

  .practice-choice {
    width: 100%;
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 8px 9px;
    margin-top: 7px;
    background: var(--mt-surface);
    color: var(--mt-text);
    cursor: pointer;
    text-align: left;
  }

  .practice-choice.selected {
    border-color: rgba(255, 176, 0, 0.58);
    box-shadow: inset 3px 0 0 var(--mt-warning);
  }

  .practice-choice.correct {
    border-color: rgba(0, 143, 79, 0.5);
    box-shadow: inset 3px 0 0 #008f4f;
  }

  .practice-choice.wrong {
    border-color: rgba(217, 45, 32, 0.5);
    box-shadow: inset 3px 0 0 #d92d20;
  }

  .question-card {
    border-top: 1px solid var(--mt-border);
    padding-top: 12px;
    margin-top: 12px;
  }

  .question-card:first-of-type {
    border-top: 0;
    padding-top: 0;
    margin-top: 0;
  }

  .quiz-option {
    width: 100%;
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 10px 12px;
    margin-top: 8px;
    background: var(--mt-card);
    color: var(--mt-text);
    cursor: pointer;
    text-align: left;
  }

  .quiz-option.selected {
    border-color: rgba(255, 176, 0, 0.58);
    box-shadow: inset 3px 0 0 var(--mt-warning);
  }

  .quiz-option.correct {
    border-color: rgba(0, 143, 79, 0.54);
    box-shadow: inset 3px 0 0 #008f4f;
  }

  .quiz-option.wrong {
    border-color: rgba(217, 45, 32, 0.5);
    box-shadow: inset 3px 0 0 #d92d20;
  }

  .quiz-result {
    min-height: 22px;
    margin-top: 10px;
    color: var(--mt-muted);
    font-family: Poppins-B;
    font-size: 12px;
  }

  .drag-layout {
    display: grid;
    grid-template-columns: 0.9fr 1.1fr;
    gap: 10px;
  }

  .drag-items,
  .drop-zones {
    display: grid;
    gap: 8px;
  }

  .drag-pill,
  .drop-zone {
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 10px;
    background: var(--mt-card);
  }

  .drag-pill {
    color: var(--mt-warning);
    cursor: grab;
    font-family: Poppins-B;
  }

  .drop-zone {
    min-height: 54px;
  }

  .drop-zone span {
    display: block;
    color: var(--mt-muted);
    font-size: 11px;
  }

  .drop-zone strong {
    display: block;
    margin-top: 4px;
    color: var(--mt-text);
    font-family: Poppins-B;
  }

  .drop-zone.correct {
    border-color: rgba(0, 143, 79, 0.5);
    box-shadow: inset 3px 0 0 #008f4f;
  }

  .game-actions {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    margin-top: 12px;
  }

  .certificate-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
    margin-top: 12px;
  }

  .certificate-stat {
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 10px;
    background: var(--mt-card);
  }

  .certificate-stat span {
    display: block;
    color: var(--mt-muted);
    font-size: 10px;
    text-transform: uppercase;
  }

  .certificate-stat strong {
    display: block;
    margin-top: 4px;
    color: var(--mt-warning);
    font-family: Poppins-B;
    font-size: 18px;
  }

  .mastery-list {
    display: grid;
    gap: 6px;
    margin-top: 12px;
    color: var(--mt-text);
    font-size: 12px;
  }

  .quick-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 12px;
  }

  .onboarding-panel,
  .market-board,
  .history-box,
  .simulation-box {
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 16px;
    background: var(--mt-surface);
  }

  .onboarding-panel {
    margin: 12px 12px 0;
  }

  .market-board,
  .history-box,
  .simulation-box {
    margin-top: 12px;
  }

  .onboarding-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
  }

  .onboarding-header h3,
  .market-board h3,
  .history-box h3,
  .simulation-box h3 {
    margin: 4px 0 8px;
    color: var(--mt-text);
    font-family: Poppins-B;
    font-size: 20px;
  }

  .onboarding-header p,
  .market-board p,
  .history-box p,
  .simulation-box p {
    margin: 0;
    color: var(--mt-muted);
    line-height: 1.55;
  }

  .onboarding-steps,
  .market-grid,
  .history-list,
  .simulation-grid {
    display: grid;
    gap: 8px;
    margin-top: 12px;
  }

  .onboarding-steps {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  .onboarding-step,
  .market-row,
  .history-row,
  .simulation-card {
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 10px;
    background: var(--mt-card);
  }

  .onboarding-step span,
  .market-row span,
  .history-row span,
  .simulation-card span {
    display: block;
    color: var(--mt-warning);
    font-family: Poppins-B;
    font-size: 10px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .onboarding-step strong,
  .market-row strong,
  .history-row strong,
  .simulation-card strong {
    display: block;
    margin-top: 5px;
    color: var(--mt-text);
    font-family: Poppins-B;
  }

  .market-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .market-row em,
  .history-row em {
    display: block;
    margin-top: 4px;
    color: var(--mt-muted);
    font-style: normal;
    font-size: 11px;
  }

  .simulation-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .simulation-choice {
    width: 100%;
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 9px 10px;
    margin-top: 7px;
    background: var(--mt-surface);
    color: var(--mt-text);
    cursor: pointer;
    text-align: left;
  }

  .simulation-choice.selected {
    border-color: rgba(255, 176, 0, 0.58);
    box-shadow: inset 3px 0 0 var(--mt-warning);
  }

  .simulation-choice.correct {
    border-color: rgba(0, 143, 79, 0.5);
    box-shadow: inset 3px 0 0 #008f4f;
  }

  .simulation-choice.wrong {
    border-color: rgba(217, 45, 32, 0.5);
    box-shadow: inset 3px 0 0 #d92d20;
  }

  .premium-button {
    border: 0;
    border-radius: 999px;
    background: linear-gradient(135deg, ${ORANGE}, ${YELLOW});
    color: #07141c;
    font-family: Poppins-B;
  }

  @media (max-width: 1100px) {
    .learning-content,
    .tip-screen,
    .learning-grid,
    .certificate-grid,
    .detail-metrics,
    .practice-grid,
    .onboarding-steps,
    .market-grid,
    .simulation-grid {
      grid-template-columns: 1fr;
    }
  }
`;

type Level = 'all' | 'debutant' | 'intermediaire' | 'desk';

type LessonLevel = Exclude<Level, 'all'>;

type Lesson = {
  step: string;
  level: LessonLevel;
  title: string;
  summary: string;
  body: string;
  points: string[];
  tip: string;
  note: string;
};

type SavedProgress = {
  activeLesson: number;
  selectedLevel: Level;
  quizAnswers: Record<string, string>;
  matches: Record<string, string>;
};

type Quiz = {
  question: string;
  options: string[];
  answer: string;
  success: string;
  correction: string;
};

type PracticeCase = {
  title: string;
  scenario: string;
  fields: {
    key: string;
    label: string;
    options: string[];
    answer: string;
  }[];
  success: string;
};

type SimulationStep = {
  key: string;
  label: string;
  options: string[];
  answer: string;
};

const PROGRESS_STORAGE_KEY = 'moranjetrade-training-progress';
const ONBOARDING_STORAGE_KEY = 'moranjetrade-training-onboarding';

const LEVEL_LABELS: Record<LessonLevel, string> = {
  debutant: 'Débutant',
  intermediaire: 'Intermédiaire',
  desk: 'Desk trader',
};

const LEVELS: { label: string; value: Level }[] = [
  { label: 'Tout', value: 'all' },
  { label: 'Débutant', value: 'debutant' },
  { label: 'Intermédiaire', value: 'intermediaire' },
  { label: 'Desk trader', value: 'desk' },
];

const getSavedProgress = (): SavedProgress => {
  if (typeof window === 'undefined') {
    return {
      activeLesson: 0,
      selectedLevel: 'all',
      quizAnswers: {},
      matches: {},
    };
  }

  try {
    const saved = localStorage.getItem(PROGRESS_STORAGE_KEY);
    if (!saved) {
      return {
        activeLesson: 0,
        selectedLevel: 'all',
        quizAnswers: {},
        matches: {},
      };
    }

    const parsed = JSON.parse(saved) as Partial<SavedProgress>;
    const activeLesson =
      typeof parsed.activeLesson === 'number' &&
      parsed.activeLesson >= 0 &&
      parsed.activeLesson < LESSONS.length
        ? parsed.activeLesson
        : 0;
    const selectedLevel = LEVELS.some((level) => level.value === parsed.selectedLevel)
      ? (parsed.selectedLevel as Level)
      : 'all';

    return {
      activeLesson,
      selectedLevel,
      quizAnswers: parsed.quizAnswers || {},
      matches: parsed.matches || {},
    };
  } catch {
    return {
      activeLesson: 0,
      selectedLevel: 'all',
      quizAnswers: {},
      matches: {},
    };
  }
};

const LESSON_DETAILS = [
  {
    title: 'Lire le rendement derrière le prix',
    explanation:
      "Une obligation ne se lit pas uniquement par son coupon. Le rendement dépend du prix payé, des coupons futurs, du remboursement final et du temps restant jusqu'à l'échéance.",
    formula: 'Rendement = flux futurs actualisés / prix payé',
    example:
      'Un titre à 98% avec coupon 5% peut être plus attractif qu’un titre à 101% avec le même coupon, surtout si leurs maturités sont proches.',
  },
  {
    title: 'Interpréter la pression de marché',
    explanation:
      "Le bid et l’ask montrent où se rencontrent les acheteurs et les vendeurs. La distance entre les deux prix donne une lecture rapide de la liquidité.",
    formula: 'Spread = Ask - Bid',
    example:
      'Un bid 9 800 et un ask 9 500 signalent une donnée incohérente ou inversée. Un carnet sain doit permettre une lecture claire du meilleur achat et de la meilleure vente.',
  },
  {
    title: 'Contrôler un ticket avant transmission',
    explanation:
      "Un ticket de trading doit être traité comme un ordre à risque opérationnel. Chaque champ visible doit pouvoir être justifié par le carnet, le détenteur et le règlement attendu.",
    formula: 'Net à payer = prix + coupon couru + commissions',
    example:
      'Avant de transmettre 400 OTA à 9 600 XAF, le desk vérifie le volume disponible, le détenteur, le montant global et le coupon couru.',
  },
  {
    title: 'Relier primaire et secondaire',
    explanation:
      "Le marché secondaire donne une base de comparaison aux nouvelles émissions. Il permet d’observer ce que le marché accepte réellement comme rendement.",
    formula: 'Prime nouvelle émission = rendement demandé - rendement comparable',
    example:
      'Si un titre souverain proche traite autour de 6,8%, une nouvelle émission comparable aura du mal à sortir à 5,5% sans avantage particulier.',
  },
  {
    title: 'Calculer la logique du coupon couru',
    explanation:
      "Entre deux dates de coupon, le vendeur a porté le titre pendant une partie de la période. Le coupon couru compense cette période au moment du règlement.",
    formula: 'Coupon couru = nominal x taux coupon x jours courus / base',
    example:
      'Sur un nominal de 10 000 XAF avec coupon 6%, la part courue augmente chaque jour jusqu’à la prochaine date de paiement.',
  },
  {
    title: 'Décote et prime dans la décision',
    explanation:
      "La décote ou la prime ne suffit jamais. Il faut la relier à la maturité, au coupon, à la qualité de l’émetteur et à la liquidité disponible.",
    formula: 'Prix < 100 = décote ; prix > 100 = prime',
    example:
      'Un prix de 99% peut être cher si le titre est très court et peu liquide, mais intéressant sur une maturité plus longue avec coupon élevé.',
  },
  {
    title: 'Construire une lecture de courbe',
    explanation:
      "Une courbe de taux transforme des titres isolés en structure de marché. Elle aide à voir si le rendement demandé est cohérent avec la maturité.",
    formula: 'Point de courbe = maturité résiduelle + rendement actuariel',
    example:
      'Un titre 2 ans à 7% peut être une anomalie si les autres titres 2 ans du même pays traitent autour de 5,8%.',
  },
  {
    title: 'Sécuriser le règlement-livraison',
    explanation:
      "Une transaction n’est réellement terminée qu’après échange correct des espèces et des titres. Le front doit donc préparer un ticket lisible pour le back-office.",
    formula: 'Front-office -> contrôle -> back-office -> dépositaire',
    example:
      'Un mauvais détenteur peut transformer un bon ordre de marché en incident de dénouement.',
  },
];

const PRACTICE_CASES: PracticeCase[] = [
  {
    title: 'Acheter sur une offre disponible',
    scenario:
      'Vous devez acheter 400 OTA. Le carnet affiche un ask à 9 600 XAF et une quantité disponible de 500.',
    fields: [
      {
        key: 'side',
        label: 'Sens',
        options: ['Achat', 'Vente'],
        answer: 'Achat',
      },
      {
        key: 'price',
        label: 'Prix',
        options: ['9 600 XAF', '9 800 XAF', '10 000 XAF'],
        answer: '9 600 XAF',
      },
      {
        key: 'quantity',
        label: 'Quantité',
        options: ['400', '500', '900'],
        answer: '400',
      },
      {
        key: 'control',
        label: 'Contrôle',
        options: ['Volume suffisant', 'Volume insuffisant', 'Aucun détenteur requis'],
        answer: 'Volume suffisant',
      },
    ],
    success: 'Ticket cohérent : l’ordre peut être préparé.',
  },
  {
    title: 'Servir une demande d’achat',
    scenario:
      'Un acheteur demande 150 BTA à 9 500 XAF. Vous détenez le titre et vous acceptez de vendre.',
    fields: [
      {
        key: 'side',
        label: 'Sens',
        options: ['Achat', 'Vente'],
        answer: 'Vente',
      },
      {
        key: 'price',
        label: 'Prix',
        options: ['9 500 XAF', '9 800 XAF', '10 000 XAF'],
        answer: '9 500 XAF',
      },
      {
        key: 'quantity',
        label: 'Quantité',
        options: ['150', '50', '548'],
        answer: '150',
      },
      {
        key: 'control',
        label: 'Contrôle',
        options: ['Vérifier la position détenue', 'Ignorer le portefeuille', 'Changer la maturité'],
        answer: 'Vérifier la position détenue',
      },
    ],
    success: 'Ticket cohérent : la vente peut être préparée.',
  },
  {
    title: 'Contrôler une anomalie de prix',
    scenario:
      'Un titre comparable traite à 6% de rendement, mais le ticket affiche 0%. Vous devez décider quoi faire avant transmission.',
    fields: [
      {
        key: 'side',
        label: 'Action',
        options: ['Transmettre immédiatement', 'Contrôler la formule', 'Supprimer le titre'],
        answer: 'Contrôler la formule',
      },
      {
        key: 'price',
        label: 'Point sensible',
        options: ['Rendement', 'Logo', 'Menu langue'],
        answer: 'Rendement',
      },
      {
        key: 'quantity',
        label: 'Risque',
        options: ['Mauvaise valorisation', 'Aucun risque', 'Uniquement esthétique'],
        answer: 'Mauvaise valorisation',
      },
      {
        key: 'control',
        label: 'Contrôle',
        options: ['Comparer à la courbe', 'Masquer le coupon', 'Changer de thème'],
        answer: 'Comparer à la courbe',
      },
    ],
    success: 'Bon réflexe : le ticket doit être contrôlé avant transmission.',
  },
];

const TRAINING_MARKET_DATA = [
  {
    code: 'CM110000003',
    country: 'Cameroun',
    type: 'OTA',
    maturity: '30/11/2026',
    bid: '9 800 XAF',
    ask: '9 600 XAF',
    bidQty: '150',
    askQty: '500',
    coupon: '5%',
  },
  {
    code: 'GBN110000001',
    country: 'Gabon',
    type: 'BTA',
    maturity: '30/09/2025',
    bid: '9 500 XAF',
    ask: '9 700 XAF',
    bidQty: '536',
    askQty: '120',
    coupon: '4%',
  },
  {
    code: 'CM110000002',
    country: 'Cameroun',
    type: 'OTA',
    maturity: '31/01/2028',
    bid: '9 800 XAF',
    ask: '10 000 XAF',
    bidQty: '500',
    askQty: '300',
    coupon: '6.5%',
  },
];

const SIMULATION_STEPS: SimulationStep[] = [
  {
    key: 'instrument',
    label: 'Titre à traiter',
    options: TRAINING_MARKET_DATA.map((item) => item.code),
    answer: 'CM110000003',
  },
  {
    key: 'side',
    label: 'Sens de l’ordre',
    options: ['Achat', 'Vente'],
    answer: 'Achat',
  },
  {
    key: 'price',
    label: 'Prix à saisir',
    options: ['9 500 XAF', '9 600 XAF', '9 800 XAF'],
    answer: '9 600 XAF',
  },
  {
    key: 'quantity',
    label: 'Quantité à saisir',
    options: ['150', '400', '500'],
    answer: '400',
  },
];

const ONBOARDING_STEPS = [
  {
    label: 'Découvrir les titres',
    text: 'Repérer code, pays, type, coupon et maturité.',
  },
  {
    label: 'Lire le carnet',
    text: 'Comparer bid, ask, volumes et spread.',
  },
  {
    label: 'Préparer l’ordre',
    text: 'Choisir sens, prix, quantité et détenteur.',
  },
  {
    label: 'Valider le règlement',
    text: 'Contrôler coupon couru, frais et net à payer.',
  },
];

export const InfoMarchePage = () => {
  const history = useHistory();
  const savedProgress = getSavedProgress();
  const [showOnboarding, setShowOnboarding] = useState(
    () => localStorage.getItem(ONBOARDING_STORAGE_KEY) !== 'done',
  );
  const [activeLesson, setActiveLesson] = useState(savedProgress.activeLesson);
  const [selectedLevel, setSelectedLevel] = useState(savedProgress.selectedLevel);
  const [quizAnswers, setQuizAnswers] = useState<Record<string, string>>(
    savedProgress.quizAnswers,
  );
  const [draggedTerm, setDraggedTerm] = useState('');
  const [matches, setMatches] = useState<Record<string, string>>(
    savedProgress.matches,
  );
  const [practiceAnswers, setPracticeAnswers] = useState<Record<string, string>>({});
  const [simulationAnswers, setSimulationAnswers] = useState<Record<string, string>>(
    {},
  );

  const lesson = LESSONS[activeLesson];
  const detail = LESSON_DETAILS[activeLesson];
  const practiceCase = PRACTICE_CASES[activeLesson % PRACTICE_CASES.length];
  const activeQuizzes = QUIZZES[activeLesson] || [];
  const totalQuestions = QUIZZES.reduce((total, quizzes) => total + quizzes.length, 0);
  const completedLessons = LESSONS.filter(
    (_, index) =>
      QUIZZES[index].every(
        (quiz, questionIndex) =>
          quizAnswers[`${index}-${questionIndex}`] === quiz.answer,
      ),
  ).length;
  const progressRatio = Math.round((completedLessons / LESSONS.length) * 100);
  const quizScore = QUIZZES.reduce(
    (score, quizzes, lessonIndex) =>
      score +
      quizzes.filter(
        (quiz, questionIndex) =>
          quizAnswers[`${lessonIndex}-${questionIndex}`] === quiz.answer,
      ).length,
    0,
  );
  const correctMatches = useMemo(
    () => DRAG_TERMS.filter((term) => matches[term.target] === term.label).length,
    [matches],
  );
  const visibleLessons = useMemo(
    () =>
      LESSONS.map((item, index) => ({ item, index })).filter(
        ({ item }) => selectedLevel === 'all' || item.level === selectedLevel,
      ),
    [selectedLevel],
  );
  const levelStats = useMemo(
    () =>
      (LEVELS.filter((level) => level.value !== 'all') as {
        label: string;
        value: LessonLevel;
      }[]).map((level) => {
        const levelLessons = LESSONS.map((item, index) => ({ item, index })).filter(
          ({ item }) => item.level === level.value,
        );
        const completed = levelLessons.filter(({ index }) =>
          QUIZZES[index].every(
            (quiz, questionIndex) =>
              quizAnswers[`${index}-${questionIndex}`] === quiz.answer,
          ),
        ).length;

        return {
          ...level,
          completed,
          total: levelLessons.length,
        };
      }),
    [quizAnswers],
  );
  const practiceScore = practiceCase.fields.filter(
    (field) =>
      practiceAnswers[`${activeLesson}-${field.key}`] === field.answer,
  ).length;
  const simulationScore = SIMULATION_STEPS.filter(
    (step) => simulationAnswers[step.key] === step.answer,
  ).length;
  const historyRows = [
    {
      label: 'Dernière fiche consultée',
      value: lesson.title,
      meta: LEVEL_LABELS[lesson.level],
    },
    {
      label: 'Quiz validés',
      value: `${quizScore}/${totalQuestions}`,
      meta: `${completedLessons} fiches entièrement validées`,
    },
    {
      label: 'Simulation guidée',
      value: `${simulationScore}/${SIMULATION_STEPS.length}`,
      meta:
        simulationScore === SIMULATION_STEPS.length
          ? 'Ticket simulé correctement'
          : 'Simulation à finaliser',
    },
  ];

  useEffect(() => {
    localStorage.setItem(
      PROGRESS_STORAGE_KEY,
      JSON.stringify({
        activeLesson,
        selectedLevel,
        quizAnswers,
        matches,
      }),
    );
  }, [activeLesson, matches, quizAnswers, selectedLevel]);

  const moveLesson = (direction: number) => {
    setActiveLesson((current) =>
      Math.min(Math.max(current + direction, 0), LESSONS.length - 1),
    );
  };

  const resetActivities = () => {
    setQuizAnswers({});
    setMatches({});
    setPracticeAnswers({});
    setSimulationAnswers({});
    setDraggedTerm('');
    localStorage.removeItem(PROGRESS_STORAGE_KEY);
  };

  const closeOnboarding = () => {
    setShowOnboarding(false);
    localStorage.setItem(ONBOARDING_STORAGE_KEY, 'done');
  };

  return (
    <Layout>
      <Container>
        <section className='learning-hero'>
          <div className='hero-inner'>
            <div>
              <div className='brand-row'>
                <img src={logo} alt='MoranjETrade' />
                <strong>
                  <span className='orange'>Moranj</span>E
                  <span className='gold'>Trade</span>
                </strong>
              </div>
              <span className='hero-kicker'>Fiches et Infos</span>
              <h1 className='hero-title'>
                Apprendre le métier avec des fiches courtes et interactives.
              </h1>
              <p className='hero-copy'>
                Comprenez les notions clés du marché obligataire, testez vos
                réflexes avec des quiz rapides et entraînez-vous à relier les
                concepts comme sur un vrai desk.
              </p>
              <div className='score-strip'>
                <span className='score-pill'>
                  Quiz <strong>{quizScore}/{totalQuestions}</strong>
                </span>
                <span className='score-pill'>
                  Associations <strong>{correctMatches}/{DROP_ZONES.length}</strong>
                </span>
                <span className='score-pill'>
                  Fiches <strong>{LESSONS.length}</strong>
                </span>
              </div>
            </div>

            <div className='hero-status'>
              <span>Progression module</span>
              <strong>
                {completedLessons}/{LESSONS.length}
              </strong>
              <div className='progress-track'>
                <div
                  className='progress-fill'
                  style={{ width: `${progressRatio}%` }}
                />
              </div>
            </div>
          </div>
        </section>

        {showOnboarding && (
          <section className='onboarding-panel'>
            <div className='onboarding-header'>
              <div>
                <span className='hero-kicker'>Onboarding</span>
                <h3>Votre parcours de prise en main MoranjETrade</h3>
                <p>
                  Une progression courte pour passer de la lecture des titres à la
                  préparation d’un ticket de trading contrôlé.
                </p>
              </div>
              <Button className='premium-button' onClick={closeOnboarding}>
                Commencer
              </Button>
            </div>
            <div className='onboarding-steps'>
              {ONBOARDING_STEPS.map((step, index) => (
                <div className='onboarding-step' key={step.label}>
                  <span>Étape {index + 1}</span>
                  <strong>{step.label}</strong>
                  <p>{step.text}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className='learning-content'>
          <aside className='panel'>
            <div className='panel-header'>
              <span>Parcours métier</span>
              <FiBookOpen />
            </div>
            <div className='level-tabs'>
              {LEVELS.map((level) => (
                <button
                  className={`level-tab ${
                    selectedLevel === level.value ? 'active' : ''
                  }`}
                  key={level.value}
                  type='button'
                  onClick={() => {
                    setSelectedLevel(level.value);
                    const nextLesson = LESSONS.findIndex(
                      (item) => level.value === 'all' || item.level === level.value,
                    );
                    setActiveLesson(Math.max(nextLesson, 0));
                  }}
                >
                  {level.label}
                </button>
              ))}
            </div>
            <div className='lesson-list'>
              {visibleLessons.map(({ item, index }) => (
                <button
                  className={`lesson-card ${
                    index === activeLesson ? 'active' : ''
                  } ${
                    QUIZZES[index].every(
                      (quiz, questionIndex) =>
                        quizAnswers[`${index}-${questionIndex}`] === quiz.answer,
                    )
                      ? 'completed'
                      : ''
                  }`}
                  key={item.title}
                  type='button'
                  onClick={() => {
                    setActiveLesson(index);
                  }}
                >
                  <div className='lesson-meta'>
                    <span>{item.step}</span>
                    <em className='level-badge'>{LEVEL_LABELS[item.level]}</em>
                  </div>
                  <strong>{item.title}</strong>
                  <p>{item.summary}</p>
                </button>
              ))}
            </div>
            <div className='roadmap'>
              <span className='roadmap-title'>Progression par niveau</span>
              {levelStats.map((level) => (
                <div className='roadmap-step' key={level.value}>
                  <strong>
                    {level.label}
                    <span>{level.completed}/{level.total}</span>
                  </strong>
                  <p>
                    {level.completed === level.total
                      ? 'Niveau maîtrisé.'
                      : 'Terminez les fiches et quiz pour consolider ce niveau.'}
                  </p>
                </div>
              ))}
            </div>
          </aside>

          <main className='panel'>
            <div className='panel-header'>
              <span>Micro-fiche active</span>
              <FiTarget />
            </div>
            <div className='lesson-stage'>
              <div className='tip-screen'>
                <article className='tip-main'>
                  <span className='eyebrow'>{lesson.step}</span>
                  <h2>{lesson.title}</h2>
                  <p>{lesson.body}</p>
                  <div className='tip-points'>
                    {lesson.points.map((point) => (
                      <div className='tip-point' key={point}>
                        <FiCheck />
                        <span>{point}</span>
                      </div>
                    ))}
                  </div>
                </article>

                <aside className='side-note'>
                  <div>
                    <span className='eyebrow'>Astuce desk</span>
                    <strong>{lesson.tip}</strong>
                    <p>{lesson.note}</p>
                  </div>
                  <Button
                    className='premium-button'
                    onClick={() => history.push(ROUTES.COURBE.THEORIE)}
                  >
                    Voir les courbes <FiArrowRight />
                  </Button>
                </aside>
              </div>

              <div className='lesson-actions'>
                <Button disabled={activeLesson === 0} onClick={() => moveLesson(-1)}>
                  Fiche précédente
                </Button>
                <Button
                  className='premium-button'
                  disabled={activeLesson === LESSONS.length - 1}
                  onClick={() => moveLesson(1)}
                >
                  Fiche suivante
                </Button>
              </div>

              <section className='detail-panel'>
                <span className='eyebrow'>Fiche détaillée</span>
                <h3>{detail.title}</h3>
                <p>{detail.explanation}</p>
                <div className='detail-metrics'>
                  <div className='detail-metric'>
                    <span>Formule / lecture</span>
                    <strong>{detail.formula}</strong>
                  </div>
                  <div className='detail-metric'>
                    <span>Exemple métier</span>
                    <strong>{detail.example}</strong>
                  </div>
                </div>
              </section>

              <section className='practice-box'>
                <span className='eyebrow'>Cas pratique</span>
                <h3>{practiceCase.title}</h3>
                <p>{practiceCase.scenario}</p>
                <div className='practice-grid'>
                  {practiceCase.fields.map((field) => {
                    const answerKey = `${activeLesson}-${field.key}`;
                    const selected = practiceAnswers[answerKey] || '';

                    return (
                      <div className='practice-field' key={field.key}>
                        <span>{field.label}</span>
                        {field.options.map((option) => (
                          <button
                            className={`practice-choice ${
                              selected === option ? 'selected' : ''
                            } ${
                              selected && option === field.answer ? 'correct' : ''
                            } ${
                              selected === option && option !== field.answer
                                ? 'wrong'
                                : ''
                            }`}
                            key={option}
                            type='button'
                            onClick={() =>
                              setPracticeAnswers({
                                ...practiceAnswers,
                                [answerKey]: option,
                              })
                            }
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    );
                  })}
                </div>
                <div className='game-actions'>
                  <span className='quiz-result'>
                    {practiceScore}/{practiceCase.fields.length} contrôles corrects
                  </span>
                  <span className='quiz-result'>
                    {practiceScore === practiceCase.fields.length
                      ? practiceCase.success
                      : 'Complétez les choix pour valider le ticket.'}
                  </span>
                </div>
              </section>

              <section className='market-board'>
                <span className='eyebrow'>Données de marché d’entraînement</span>
                <h3>Extraits réalistes du carnet pour les exercices.</h3>
                <p>
                  Ces lignes servent à générer les cas pratiques et la simulation
                  sans toucher aux ordres réels du dashboard.
                </p>
                <div className='market-grid'>
                  {TRAINING_MARKET_DATA.map((item) => (
                    <div className='market-row' key={item.code}>
                      <span>{item.country} · {item.type}</span>
                      <strong>{item.code}</strong>
                      <em>
                        Bid {item.bid} ({item.bidQty}) · Ask {item.ask} ({item.askQty})
                      </em>
                      <em>
                        Maturité {item.maturity} · Coupon {item.coupon}
                      </em>
                    </div>
                  ))}
                </div>
              </section>

              <section className='simulation-box'>
                <span className='eyebrow'>Simulation de trading</span>
                <h3>Préparez un ordre à partir du carnet.</h3>
                <p>
                  Mission : acheter 400 OTA CM110000003 au meilleur prix vendeur
                  disponible, puis vérifier que le volume du carnet suffit.
                </p>
                <div className='simulation-grid'>
                  {SIMULATION_STEPS.map((step) => {
                    const selected = simulationAnswers[step.key] || '';

                    return (
                      <div className='simulation-card' key={step.key}>
                        <span>{step.label}</span>
                        {step.options.map((option) => (
                          <button
                            className={`simulation-choice ${
                              selected === option ? 'selected' : ''
                            } ${
                              selected && option === step.answer ? 'correct' : ''
                            } ${
                              selected === option && option !== step.answer
                                ? 'wrong'
                                : ''
                            }`}
                            key={option}
                            type='button'
                            onClick={() =>
                              setSimulationAnswers({
                                ...simulationAnswers,
                                [step.key]: option,
                              })
                            }
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    );
                  })}
                </div>
                <div className='game-actions'>
                  <span className='quiz-result'>
                    Score simulation : {simulationScore}/{SIMULATION_STEPS.length}
                  </span>
                  <span className='quiz-result'>
                    {simulationScore === SIMULATION_STEPS.length
                      ? 'Simulation réussie : le ticket est cohérent.'
                      : 'Complétez les champs comme dans le terminal.'}
                  </span>
                </div>
              </section>

              <div className='learning-grid'>
                <section className='quiz-box'>
                  <span className='eyebrow'>Quiz rapide</span>
                  <h3>Validez les réflexes de la fiche.</h3>
                  {activeQuizzes.map((activeQuiz, questionIndex) => {
                    const answerKey = `${activeLesson}-${questionIndex}`;
                    const quizAnswer = quizAnswers[answerKey] || '';

                    return (
                      <div className='question-card' key={activeQuiz.question}>
                        <h3>{activeQuiz.question}</h3>
                        {activeQuiz.options.map((option) => (
                          <button
                            className={`quiz-option ${
                              quizAnswer === option ? 'selected' : ''
                            } ${
                              quizAnswer && option === activeQuiz.answer
                                ? 'correct'
                                : ''
                            } ${
                              quizAnswer === option && option !== activeQuiz.answer
                                ? 'wrong'
                                : ''
                            }`}
                            key={option}
                            type='button'
                            onClick={() =>
                              setQuizAnswers({
                                ...quizAnswers,
                                [answerKey]: option,
                              })
                            }
                          >
                            {option}
                          </button>
                        ))}
                        <div className='quiz-result'>
                          {quizAnswer
                            ? quizAnswer === activeQuiz.answer
                              ? activeQuiz.success
                              : activeQuiz.correction
                            : 'Sélectionnez une réponse pour vérifier.'}
                        </div>
                      </div>
                    );
                  })}
                  <div className='game-actions'>
                    <Button onClick={resetActivities}>Réinitialiser</Button>
                    <span className='quiz-result'>
                      Score : {quizScore}/{totalQuestions}
                    </span>
                  </div>
                </section>

                <section className='drag-box'>
                  <span className='eyebrow'>Mini-jeu drag and drop</span>
                  <h3>Associez chaque terme à sa définition.</h3>
                  <div className='drag-layout'>
                    <div className='drag-items'>
                      {DRAG_TERMS.map((term) => (
                        <div
                          className='drag-pill'
                          draggable
                          key={term.label}
                          onDragStart={() => setDraggedTerm(term.label)}
                        >
                          <FiMove /> {term.label}
                        </div>
                      ))}
                    </div>
                    <div className='drop-zones'>
                      {DROP_ZONES.map((zone) => (
                        <div
                          className={`drop-zone ${
                            matches[zone.id] === zone.answer ? 'correct' : ''
                          }`}
                          key={zone.id}
                          onDragOver={(event) => event.preventDefault()}
                          onDrop={() => {
                            if (!draggedTerm) return;
                            setMatches({ ...matches, [zone.id]: draggedTerm });
                          }}
                        >
                          <span>{zone.label}</span>
                          <strong>{matches[zone.id] || 'Déposez un terme ici'}</strong>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className='quiz-result'>
                    {correctMatches}/{DROP_ZONES.length} associations correctes
                  </div>
                  <div className='game-actions'>
                    <Button onClick={() => setMatches({})}>Recommencer</Button>
                    <Button
                      className='premium-button'
                      onClick={() =>
                        setMatches(
                          DROP_ZONES.reduce<Record<string, string>>(
                            (acc, zone) => ({ ...acc, [zone.id]: zone.answer }),
                            {},
                          ),
                        )
                      }
                    >
                      Voir correction
                    </Button>
                  </div>
                </section>
              </div>

              <div className='learning-grid'>
                <section className='drag-box'>
                  <span className='eyebrow'>Atelier ticket d’ordre</span>
                  <h3>Replacez les étapes dans la bonne zone.</h3>
                  <div className='drag-layout'>
                    <div className='drag-items'>
                      {TICKET_TERMS.map((term) => (
                        <div
                          className='drag-pill'
                          draggable
                          key={term.label}
                          onDragStart={() => setDraggedTerm(term.label)}
                        >
                          <FiMove /> {term.label}
                        </div>
                      ))}
                    </div>
                    <div className='drop-zones'>
                      {TICKET_ZONES.map((zone) => (
                        <div
                          className={`drop-zone ${
                            matches[zone.id] === zone.answer ? 'correct' : ''
                          }`}
                          key={zone.id}
                          onDragOver={(event) => event.preventDefault()}
                          onDrop={() => {
                            if (!draggedTerm) return;
                            setMatches({ ...matches, [zone.id]: draggedTerm });
                          }}
                        >
                          <span>{zone.label}</span>
                          <strong>{matches[zone.id] || 'Déposez une étape ici'}</strong>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className='quiz-result'>
                    {
                      TICKET_TERMS.filter(
                        (term) => matches[term.target] === term.label,
                      ).length
                    }
                    /{TICKET_ZONES.length} étapes bien placées
                  </div>
                  <div className='game-actions'>
                    <Button
                      onClick={() =>
                        setMatches(
                          Object.keys(matches).reduce<Record<string, string>>(
                            (acc, key) =>
                              key.startsWith('ticket-')
                                ? acc
                                : { ...acc, [key]: matches[key] },
                            {},
                          ),
                        )
                      }
                    >
                      Recommencer
                    </Button>
                    <Button
                      className='premium-button'
                      onClick={() =>
                        setMatches({
                          ...matches,
                          ...TICKET_ZONES.reduce<Record<string, string>>(
                            (acc, zone) => ({ ...acc, [zone.id]: zone.answer }),
                            {},
                          ),
                        })
                      }
                    >
                      Voir correction
                    </Button>
                  </div>
                </section>

                <section className='certificate-box'>
                  <span className='eyebrow'>Validation interne</span>
                  <h3>{progressRatio >= 80 ? 'Niveau certifiable' : 'Progression en cours'}</h3>
                  <p className='quiz-result'>
                    {progressRatio >= 80
                      ? 'Le parcours est suffisamment maîtrisé pour passer à la pratique accompagnée.'
                      : 'Terminez les fiches et les quiz pour débloquer un niveau de maîtrise plus solide.'}
                  </p>
                  <div className='certificate-grid'>
                    <div className='certificate-stat'>
                      <span>Fiches validées</span>
                      <strong>{completedLessons}/{LESSONS.length}</strong>
                    </div>
                    <div className='certificate-stat'>
                      <span>Score quiz</span>
                      <strong>{quizScore}/{totalQuestions}</strong>
                    </div>
                    <div className='certificate-stat'>
                      <span>Niveau</span>
                      <strong>{progressRatio >= 80 ? 'Avancé' : 'En cours'}</strong>
                    </div>
                  </div>
                  <div className='mastery-list'>
                    {LESSONS.filter((_, index) =>
                      QUIZZES[index].every(
                        (quiz, questionIndex) =>
                          quizAnswers[`${index}-${questionIndex}`] === quiz.answer,
                      ),
                    )
                      .slice(0, 4)
                      .map((item) => (
                        <span key={item.title}>✓ {item.title}</span>
                      ))}
                    {completedLessons === 0 && (
                      <span>Aucune notion validée pour le moment.</span>
                    )}
                  </div>
                </section>
              </div>

              <section className='quick-links'>
                <span className='eyebrow'>Passer à la pratique</span>
                <h3>Relier la formation au terminal.</h3>
                <div className='quick-actions'>
                  <Button
                    className='premium-button'
                    onClick={() => history.push(ROUTES.DASHBOARD.PAGE)}
                  >
                    Ouvrir le dashboard
                  </Button>
                  <Button onClick={() => history.push(ROUTES.COURBE.GRAPHES)}>
                    Voir les courbes de taux
                  </Button>
                  <Button onClick={() => history.push(ROUTES.REPORTING.TITRE)}>
                    Voir les titres
                  </Button>
                </div>
              </section>

              <section className='history-box'>
                <span className='eyebrow'>Historique de progression</span>
                <h3>Résumé de votre dernière activité.</h3>
                <div className='history-list'>
                  {historyRows.map((row) => (
                    <div className='history-row' key={row.label}>
                      <span>{row.label}</span>
                      <strong>{row.value}</strong>
                      <em>{row.meta}</em>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          </main>
        </section>
      </Container>
    </Layout>
  );
};

const LESSONS: Lesson[] = [
  {
    step: 'Fiche 01',
    level: 'debutant',
    title: 'Comprendre une obligation',
    summary: 'Nominal, coupon, maturité et rendement.',
    body:
      "Une obligation est un titre de dette. L'investisseur prête de l'argent à un émetteur et reçoit des coupons, puis le remboursement du nominal à l'échéance.",
    points: [
      'Le nominal sert de base au calcul des coupons.',
      'La maturité indique la date de remboursement.',
      'Le rendement dépend du prix payé et des flux futurs.',
    ],
    tip: 'Prix bas, rendement haut.',
    note:
      "Quand le prix d'achat baisse sous le nominal, le rendement augmente généralement, car l'investisseur paie moins pour des flux futurs proches.",
  },
  {
    step: 'Fiche 02',
    level: 'debutant',
    title: 'Lire le Bid / Ask',
    summary: "Demande d'achat, offre de vente et spread.",
    body:
      "Le bid est le meilleur prix auquel un acheteur est prêt à acheter. L'ask est le meilleur prix auquel un vendeur est prêt à vendre. L'écart entre les deux est le spread.",
    points: [
      'Un bid élevé indique une demande plus agressive.',
      'Un ask bas indique une offre de vente plus compétitive.',
      'Le spread mesure une partie de la liquidité du titre.',
    ],
    tip: 'Le carnet raconte la pression du marché.',
    note:
      "Sur un desk, observer l'évolution du bid, de l'ask et des quantités aide à détecter l'intérêt réel sur un titre.",
  },
  {
    step: 'Fiche 03',
    level: 'debutant',
    title: 'Exécuter un ordre',
    summary: 'Choisir le sens, le prix et la quantité.',
    body:
      "Un ordre doit préciser le titre, le sens de l'opération, la quantité et le prix. Le ticket d'ordre calcule ensuite les commissions, coupons courus et montants de règlement.",
    points: [
      'Acheter une offre revient à prendre le meilleur ask.',
      'Vendre à une demande revient à servir le meilleur bid.',
      'La quantité disponible limite ce qui peut être exécuté.',
    ],
    tip: 'Préparez toujours le règlement avant de transmettre.',
    note:
      "Un ticket complet évite les erreurs opérationnelles : prix, quantité, détenteur et total de règlement doivent être cohérents.",
  },
  {
    step: 'Fiche 04',
    level: 'intermediaire',
    title: 'Marché primaire et secondaire',
    summary: 'Comprendre où naissent et où circulent les titres.',
    body:
      "Le marché primaire correspond à l'émission initiale d'un titre. Le marché secondaire permet ensuite aux investisseurs d'acheter ou de vendre ce titre entre eux.",
    points: [
      "Au primaire, l'émetteur reçoit le financement.",
      'Au secondaire, les prix reflètent la liquidité et les anticipations.',
      'Un bon suivi secondaire aide à mieux préparer les prochaines émissions.',
    ],
    tip: 'Le secondaire donne le ton du primaire.',
    note:
      "Quand un titre similaire se traite avec un rendement élevé au secondaire, le marché demandera souvent une prime sur une nouvelle émission.",
  },
  {
    step: 'Fiche 05',
    level: 'intermediaire',
    title: 'Coupon couru',
    summary: 'Pourquoi il apparaît dans le règlement.',
    body:
      "Le coupon couru représente la part d'intérêt accumulée depuis le dernier paiement de coupon. Lors d'une transaction, l'acheteur indemnise le vendeur pour cette période déjà écoulée.",
    points: [
      'Il dépend du coupon, du nominal et du nombre de jours courus.',
      "Il s'ajoute généralement au prix de règlement.",
      'Il évite de léser le vendeur entre deux dates de coupon.',
    ],
    tip: 'Le prix affiché ne raconte pas tout le règlement.',
    note:
      "Deux transactions au même prix peuvent avoir des montants à payer différents si leurs dates de règlement ne sont pas les mêmes.",
  },
  {
    step: 'Fiche 06',
    level: 'intermediaire',
    title: 'Décote, prime et pair',
    summary: 'Lire rapidement le niveau du prix.',
    body:
      "Une obligation traite au pair quand son prix est proche de 100% du nominal, avec décote quand elle traite en dessous et avec prime quand elle traite au-dessus.",
    points: [
      'Une décote peut augmenter le rendement actuariel.',
      'Une prime peut réduire le rendement si les flux futurs ne compensent pas.',
      'Le niveau de prix doit toujours être lu avec la maturité et le coupon.',
    ],
    tip: 'Prix, coupon et maturité se lisent ensemble.',
    note:
      "Un prix de 98% n'a pas la même lecture sur un titre qui rembourse dans trois mois que sur un titre qui rembourse dans cinq ans.",
  },
  {
    step: 'Fiche 07',
    level: 'desk',
    title: 'Courbe des taux',
    summary: 'Transformer plusieurs maturités en signal marché.',
    body:
      "La courbe des taux relie des maturités différentes à leurs rendements. Elle sert à comparer les titres, repérer les anomalies et estimer le niveau de rémunération exigé par le marché.",
    points: [
      'Chaque point combine une maturité et un rendement.',
      'La pente indique si le marché exige plus de rendement sur le long terme.',
      'Une courbe fiable évite de comparer des titres isolés sans contexte.',
    ],
    tip: 'Un titre se juge contre sa courbe.',
    note:
      "Si un titre offre un rendement très éloigné des titres comparables, il faut comprendre si l'écart vient du risque, de la liquidité ou d'une opportunité.",
  },
  {
    step: 'Fiche 08',
    level: 'desk',
    title: 'Règlement-livraison',
    summary: "Passer de l'ordre au mouvement réel.",
    body:
      "Le règlement-livraison est l'étape où les espèces et les titres sont effectivement échangés. C'est le moment où le ticket devient une opération comptable et dépositaire.",
    points: [
      'Le règlement couvre le montant net à payer ou à recevoir.',
      'La livraison confirme le transfert du titre.',
      'Les erreurs de détenteur, prix ou quantité peuvent bloquer le dénouement.',
    ],
    tip: 'Le vrai risque opérationnel commence après le clic.',
    note:
      "Un ordre bien saisi doit être vérifiable par le front, le back-office et le dépositaire sans retraitement manuel.",
  },
];

const QUIZZES: Quiz[][] = [
  [
    {
      question: 'Quel élément est remboursé à l’échéance ?',
      options: ['Le nominal', 'Le spread', 'La commission de courtage'],
      answer: 'Le nominal',
      success: 'Exact. Le nominal est la base remboursée à maturité.',
      correction: 'À l’échéance, l’émetteur rembourse le nominal.',
    },
    {
      question: 'Le coupon correspond principalement...',
      options: [
        'Au revenu périodique versé au porteur',
        'Au prix de marché du titre',
        'Au nombre de jours de cotation',
      ],
      answer: 'Au revenu périodique versé au porteur',
      success: 'Oui. Le coupon rémunère le porteur du titre.',
      correction: 'Le coupon est le revenu périodique versé au porteur.',
    },
    {
      question: 'Quand le prix baisse sous le nominal, le rendement tend à...',
      options: ['Augmenter', 'Disparaître', 'Toujours rester fixe'],
      answer: 'Augmenter',
      success: 'Correct. Un prix plus bas augmente souvent le rendement.',
      correction: 'À flux futurs comparables, un prix plus bas augmente le rendement.',
    },
  ],
  [
    {
      question: 'Dans un carnet d’ordres, que représente le Bid ?',
      options: [
        "Le prix auquel un vendeur veut vendre",
        "Le meilleur prix d'achat affiché par le marché",
        "Le coupon annuel de l'obligation",
      ],
      answer: "Le meilleur prix d'achat affiché par le marché",
      success: 'Exact. Le bid est la meilleure demande visible.',
      correction: "Le bid correspond au meilleur prix d'achat affiché par le marché.",
    },
    {
      question: 'Que représente l’Ask ?',
      options: [
        'La meilleure offre de vente disponible',
        'La date de maturité',
        'Le compte espèces du client',
      ],
      answer: 'La meilleure offre de vente disponible',
      success: 'Oui. L’ask est le prix vendeur affiché.',
      correction: 'L’ask représente la meilleure offre de vente disponible.',
    },
    {
      question: 'Que mesure principalement le spread Bid / Ask ?',
      options: [
        'Une partie du coût de liquidité',
        'La date de remboursement',
        'Le nombre de coupons déjà payés',
      ],
      answer: 'Une partie du coût de liquidité',
      success: 'Exact. Plus le spread est large, plus la liquidité est coûteuse.',
      correction: 'Le spread Bid / Ask mesure une partie du coût de liquidité.',
    },
  ],
  [
    {
      question: "Que signifie prendre une offre dans une logique de trading ?",
      options: [
        'Acheter au meilleur ask disponible',
        'Vendre au meilleur bid disponible',
        'Annuler le coupon couru',
      ],
      answer: 'Acheter au meilleur ask disponible',
      success: "Oui. Vous acceptez le prix du vendeur.",
      correction: "Prendre une offre revient à acheter au meilleur ask disponible.",
    },
    {
      question: 'Quel champ limite l’exécution possible ?',
      options: ['La quantité disponible', 'Le nom du menu', 'La couleur du badge'],
      answer: 'La quantité disponible',
      success: 'Exact. On ne peut pas exécuter plus que le volume disponible.',
      correction: 'La quantité disponible limite le volume exécutable.',
    },
    {
      question: 'Avant transmission, le ticket doit surtout vérifier...',
      options: [
        'Sens, prix, quantité, détenteur et règlement',
        'Uniquement le logo',
        'Seulement la taille du bouton',
      ],
      answer: 'Sens, prix, quantité, détenteur et règlement',
      success: 'Correct. Ce sont les contrôles critiques du ticket.',
      correction: 'Un ticket doit vérifier sens, prix, quantité, détenteur et règlement.',
    },
  ],
  [
    {
      question: 'Sur quel marché les titres déjà émis se négocient-ils ?',
      options: ['Marché secondaire', 'Marché primaire', 'Marché monétaire uniquement'],
      answer: 'Marché secondaire',
      success: 'Exact. Le secondaire fait circuler les titres après émission.',
      correction: 'Les titres déjà émis se négocient sur le marché secondaire.',
    },
    {
      question: 'Au marché primaire, le financement va principalement...',
      options: ["À l'émetteur", 'Au vendeur secondaire', 'Au service design'],
      answer: "À l'émetteur",
      success: 'Oui. Le primaire finance directement l’émetteur.',
      correction: "Au primaire, l'émetteur reçoit le financement.",
    },
    {
      question: 'Pourquoi observer le secondaire avant une émission ?',
      options: [
        'Pour estimer le rendement exigé par le marché',
        'Pour supprimer les coupons',
        'Pour éviter toute cotation',
      ],
      answer: 'Pour estimer le rendement exigé par le marché',
      success: 'Correct. Le secondaire donne un repère de pricing.',
      correction: 'Le secondaire aide à estimer le rendement demandé par le marché.',
    },
  ],
  [
    {
      question: 'À quoi sert le coupon couru dans le montant de règlement ?',
      options: [
        "À indemniser le vendeur pour les intérêts déjà accumulés",
        "À modifier le code ISIN du titre",
        'À supprimer les commissions de courtage',
      ],
      answer: "À indemniser le vendeur pour les intérêts déjà accumulés",
      success: 'Correct. Il rééquilibre les intérêts entre acheteur et vendeur.',
      correction:
        "Le coupon couru indemnise le vendeur pour les intérêts accumulés depuis le dernier coupon.",
    },
    {
      question: 'Le coupon couru dépend notamment...',
      options: [
        'Du coupon, du nominal et du temps écoulé',
        'Du nombre de menus visibles',
        'Uniquement du pays',
      ],
      answer: 'Du coupon, du nominal et du temps écoulé',
      success: 'Exact. Ces paramètres déterminent la part courue.',
      correction: 'Le coupon couru dépend du coupon, du nominal et des jours courus.',
    },
    {
      question: 'Dans le règlement, le coupon couru est généralement...',
      options: ['Ajouté au montant à payer', 'Toujours ignoré', 'Converti en maturité'],
      answer: 'Ajouté au montant à payer',
      success: 'Oui. Il entre dans le montant de règlement.',
      correction: 'Le coupon couru est généralement ajouté au montant à payer.',
    },
  ],
  [
    {
      question: "Une obligation traitant sous 100% du nominal est généralement dite...",
      options: ['Avec décote', 'Avec prime', 'Sans maturité'],
      answer: 'Avec décote',
      success: 'Exact. Sous le pair, le titre traite avec décote.',
      correction: 'Sous 100% du nominal, on parle généralement de décote.',
    },
    {
      question: 'Une obligation au-dessus de 100% traite...',
      options: ['Avec prime', 'Sans coupon', 'Hors marché'],
      answer: 'Avec prime',
      success: 'Correct. Au-dessus du pair, on parle de prime.',
      correction: 'Au-dessus de 100% du nominal, une obligation traite avec prime.',
    },
    {
      question: 'Pour lire correctement une décote, il faut regarder aussi...',
      options: ['La maturité et le coupon', 'Uniquement le pays', 'Le mot de passe'],
      answer: 'La maturité et le coupon',
      success: 'Exact. Le prix seul ne suffit pas.',
      correction: 'Une décote se lit avec la maturité, le coupon et le risque.',
    },
  ],
  [
    {
      question: 'Pourquoi construit-on une courbe des taux ?',
      options: [
        'Pour comparer les rendements par maturité',
        'Pour remplacer le carnet d’ordres',
        'Pour masquer les titres illiquides',
      ],
      answer: 'Pour comparer les rendements par maturité',
      success: 'Oui. La courbe donne le contexte de valorisation.',
      correction: 'La courbe sert à comparer les rendements selon les maturités.',
    },
    {
      question: 'Chaque point de la courbe combine...',
      options: ['Une maturité et un rendement', 'Un logo et une couleur', 'Un mot de passe et un profil'],
      answer: 'Une maturité et un rendement',
      success: 'Exact. C’est la base de lecture de la courbe.',
      correction: 'Un point de courbe combine une maturité et un rendement.',
    },
    {
      question: 'Une courbe sert aussi à repérer...',
      options: ['Les anomalies de pricing', 'Les menus désactivés', 'Les images manquantes'],
      answer: 'Les anomalies de pricing',
      success: 'Correct. Elle aide à détecter les titres atypiques.',
      correction: 'La courbe aide à repérer les anomalies de valorisation.',
    },
  ],
  [
    {
      question: "Quel risque une erreur de détenteur crée-t-elle au règlement-livraison ?",
      options: [
        'Un blocage ou un mauvais dénouement de l’opération',
        'Une hausse automatique du coupon',
        'Une disparition de la maturité',
      ],
      answer: 'Un blocage ou un mauvais dénouement de l’opération',
      success: 'Correct. Le back-office doit pouvoir dénouer sans ambiguïté.',
      correction:
        'Une erreur de détenteur peut bloquer ou mal orienter le règlement-livraison.',
    },
    {
      question: 'La livraison confirme principalement...',
      options: ['Le transfert effectif du titre', 'La suppression du coupon', 'Le changement de langue'],
      answer: 'Le transfert effectif du titre',
      success: 'Exact. La livraison porte sur le titre.',
      correction: 'La livraison confirme le transfert effectif du titre.',
    },
    {
      question: 'Le règlement couvre principalement...',
      options: ['Le mouvement espèces', 'La création du logo', 'Le nom du navigateur'],
      answer: 'Le mouvement espèces',
      success: 'Oui. Le règlement porte sur le cash.',
      correction: 'Le règlement correspond au mouvement espèces.',
    },
  ],
];

const DRAG_TERMS = [
  { label: 'Bid', target: 'buy' },
  { label: 'Ask', target: 'sell' },
  { label: 'Coupon', target: 'income' },
  { label: 'Maturité', target: 'date' },
  { label: 'Décote', target: 'discount' },
  { label: 'Courbe des taux', target: 'curve' },
];

const DROP_ZONES = [
  {
    id: 'buy',
    label: "Meilleure demande d'achat",
    answer: 'Bid',
  },
  {
    id: 'sell',
    label: 'Meilleure offre de vente',
    answer: 'Ask',
  },
  {
    id: 'income',
    label: 'Revenu périodique du titre',
    answer: 'Coupon',
  },
  {
    id: 'date',
    label: 'Date finale de remboursement',
    answer: 'Maturité',
  },
  {
    id: 'discount',
    label: 'Prix inférieur au pair',
    answer: 'Décote',
  },
  {
    id: 'curve',
    label: 'Lecture des rendements par maturité',
    answer: 'Courbe des taux',
  },
];

const TICKET_TERMS = [
  { label: 'Identifier le titre', target: 'ticket-title' },
  { label: 'Choisir achat ou vente', target: 'ticket-side' },
  { label: 'Contrôler prix et quantité', target: 'ticket-price' },
  { label: 'Valider le détenteur', target: 'ticket-holder' },
  { label: 'Vérifier le net à payer', target: 'ticket-net' },
];

const TICKET_ZONES = [
  {
    id: 'ticket-title',
    label: 'Étape 1',
    answer: 'Identifier le titre',
  },
  {
    id: 'ticket-side',
    label: 'Étape 2',
    answer: 'Choisir achat ou vente',
  },
  {
    id: 'ticket-price',
    label: 'Étape 3',
    answer: 'Contrôler prix et quantité',
  },
  {
    id: 'ticket-holder',
    label: 'Étape 4',
    answer: 'Valider le détenteur',
  },
  {
    id: 'ticket-net',
    label: 'Étape 5',
    answer: 'Vérifier le net à payer',
  },
];
